import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { NavItem, type NavItemProps } from "~/components/global-nav/NavItem";
import { cn } from "~/lib/utils";

export interface OptionDialogTab {
  value: string;
  label: string;
  items: NavItemProps[];
}

export interface OptionDialogAction {
  label: string;
  onClick?: () => void;
}

export interface OptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  hideTitle?: boolean;
  subtext?: string;
  items?: NavItemProps[];
  tabs?: OptionDialogTab[];
  primaryAction?: OptionDialogAction;
  secondaryAction?: OptionDialogAction;
  className?: string;
}

const tabTriggerClassName =
  "text-text-body-secondary data-[state=active]:border-text-body data-[state=active]:text-text-body cursor-pointer border-b-2 border-transparent px-2 py-1 text-sm data-[state=active]:font-bold";

function OptionDialog({
  open,
  onOpenChange,
  title,
  hideTitle = false,
  subtext,
  items = [],
  tabs,
  primaryAction,
  secondaryAction,
  className,
}: OptionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn("flex w-[358px] max-w-[358px] flex-col rounded-xl", className)}>
        {(title || subtext) && (
          <div className="flex flex-col gap-2">
            {title && (
              <DialogTitle className={hideTitle ? "sr-only" : "text-left text-lg font-bold"}>{title}</DialogTitle>
            )}
            {subtext && (
              <DialogDescription className="text-text-body-alt text-left text-sm">{subtext}</DialogDescription>
            )}
          </div>
        )}

        {tabs && tabs.length > 0 ? (
          <TabsPrimitive.Root defaultValue={tabs[0].value}>
            <TabsPrimitive.List className="border-border flex items-center gap-1 border-b">
              {tabs.map((tab) => (
                <TabsPrimitive.Trigger key={tab.value} value={tab.value} className={tabTriggerClassName}>
                  {tab.label}
                </TabsPrimitive.Trigger>
              ))}
            </TabsPrimitive.List>
            {tabs.map((tab) => (
              <TabsPrimitive.Content key={tab.value} value={tab.value} className="flex flex-wrap gap-4 pt-4">
                {tab.items.map((item, index) => (
                  <NavItem key={index} data-sid={`option-dialog-tab-item:${tab.value}:${item.title}`} {...item} />
                ))}
              </TabsPrimitive.Content>
            ))}
          </TabsPrimitive.Root>
        ) : (
          <div className="flex w-full flex-wrap gap-4">
            {items.map((item, index) => (
              <NavItem key={index} data-sid={`option-dialog-item:${item.title}`} {...item} />
            ))}
          </div>
        )}

        {(primaryAction || secondaryAction) && (
          <div className="flex max-h-[60px] w-full items-center justify-center gap-3">
            {secondaryAction && (
              <Button data-sid="option-dialog-secondary-action" variant="secondary" className="flex-1" onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </Button>
            )}
            {primaryAction && (
              <Button data-sid="option-dialog-primary-action" variant="primary" className="flex-1" onClick={primaryAction.onClick}>
                {primaryAction.label}
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { OptionDialog };
